import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { uploadAvatarFile } from "@/shared/lib/upload-avatar";
import { cn } from "@/shared/lib/utils";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { MonitorSmartphone, Shield, UserRound } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { profileApi } from "../api/client";
import { useAuthState } from "../model/useAuthState";
import { ActiveSessionsCard } from "../ui/ActiveSessionsCard";
import { ProfileForm } from "../ui/ProfileForm";
import { TwoFactorSetupCard } from "../ui/TwoFactorSetupCard";

export type ProfileTab = "profile" | "security" | "sessions";

const PROFILE_TABS: {
  value: ProfileTab;
  label: string;
  icon: typeof UserRound;
  description: string;
}[] = [
  {
    value: "profile",
    label: "ໂປຣໄຟລ໌",
    icon: UserRound,
    description: "ຊື່ ແລະ ຮູບໂປຣໄຟລ໌",
  },
  {
    value: "security",
    label: "ຄວາມປອດໄພ",
    icon: Shield,
    description: "2FA ແລະ ການຢືນຢັນ",
  },
  {
    value: "sessions",
    label: "ເຊສຊັນ",
    icon: MonitorSmartphone,
    description: "ອຸປະກອນທີ່ເຂົ້າສູ່ລະບົບ",
  },
];

export function ProfilePage() {
  const { user, refetch } = useAuthState();
  const { tab } = useSearch({ from: "/app/profile" });
  const navigate = useNavigate({ from: "/app/profile" });
  const [submitting, setSubmitting] = useState(false);

  const setTab = (value: string) => {
    void navigate({
      search: { tab: value as ProfileTab },
    });
  };

  const activeMeta = PROFILE_TABS.find((item) => item.value === tab);

  return (
    <>
      <Header />

      <Main>
        <div className="mb-6 space-y-1">
          <h2 className="font-bold text-2xl tracking-tight">ໂປຣໄຟລ໌ຂອງຂ້ອຍ</h2>
          <p className="text-muted-foreground text-sm">
            ຈັດການຂໍ້ມູນສ່ວນຕົວ ຄວາມປອດໄພ ແລະ ອຸປະກອນທີ່ເຂົ້າສູ່ລະບົບ
          </p>
        </div>

        <Tabs
          orientation="vertical"
          value={tab}
          onValueChange={setTab}
          className="flex flex-col gap-6 md:flex-row md:items-start"
        >
          <aside className="w-full shrink-0 md:w-56 lg:w-60">
            <TabsList
              variant="line"
              className="h-auto w-full flex-col items-stretch gap-0 rounded-xl border bg-muted/30 p-1"
            >
              {PROFILE_TABS.map((item) => {
                const Icon = item.icon;
                return (
                  <TabsTrigger
                    key={item.value}
                    value={item.value}
                    className={cn(
                      "h-auto w-full flex-none justify-start gap-3 rounded-lg px-3 py-2.5 text-left",
                      "after:inset-y-1 after:right-1 after:left-auto after:w-0.5 after:rounded-full",
                    )}
                  >
                    <Icon className="size-4 shrink-0" />
                    <span className="min-w-0">
                      <span className="block font-medium">{item.label}</span>
                      <span className="text-muted-foreground block text-xs font-normal">
                        {item.description}
                      </span>
                    </span>
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </aside>

          <div className="min-w-0 flex-1">
            {activeMeta ? (
              <div className="mb-4 md:hidden">
                <p className="font-medium">{activeMeta.label}</p>
                <p className="text-muted-foreground text-sm">
                  {activeMeta.description}
                </p>
              </div>
            ) : null}

            <TabsContent value="profile" className="mt-0 max-w-2xl">
              <Card>
                <CardHeader>
                  <CardTitle>ຂໍ້ມູນສ່ວນຕົວ</CardTitle>
                  <CardDescription>
                    ຊື່ທີ່ສະແດງໃນລະບົບ ແລະ ຮູບໂປຣໄຟລ໌
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ProfileForm
                    initialValues={{
                      name: user?.name ?? "",
                      image: user?.image ?? undefined,
                    }}
                    submitting={submitting}
                    onSubmit={async (vals) => {
                      setSubmitting(true);
                      try {
                        let imageKey: string | null | undefined =
                          vals.image ?? undefined;
                        if (vals.imageFile instanceof File) {
                          imageKey = await uploadAvatarFile(vals.imageFile);
                        }
                        const fd = new FormData();
                        fd.append("name", vals.name);
                        if (vals.password) fd.append("password", vals.password);
                        if (vals.image === null && !vals.imageFile) {
                          fd.append("imageDelete", "1");
                        } else if (typeof imageKey === "string" && imageKey) {
                          fd.append("image", imageKey);
                        }
                        await profileApi.update(fd);
                        toast.success("ອັບເດດໂປຣໄຟລ໌ສໍາເລັດ");
                        refetch();
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="security" className="mt-0 max-w-2xl">
              <TwoFactorSetupCard />
            </TabsContent>

            <TabsContent value="sessions" className="mt-0 max-w-3xl">
              <ActiveSessionsCard />
            </TabsContent>
          </div>
        </Tabs>
      </Main>
    </>
  );
}
